import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { HelpCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const FAQ = () => {
  const faqs = [
    {
      question: "Is there an entry fee for the event?",
      answer: "General admission to Paws in the Park is FREE!"
    },
    {
      question: "Can I bring multiple dogs?",
      answer: "Absolutely! We welcome all friendly, socialized dogs. Please ensure all dogs are on leash and up to date with vaccinations. You're responsible for cleaning up after your pets.",
      showEtiquetteButton: true
    },
    {
      question: "Are there facilities available at the venue?",
      answer: "Yes! Portable toilets will be located along Lakeview Drive. We also have shaded areas, water stations for dogs, and vendor booths for refreshments for their owners. The venue is accessible and family-friendly."
    },
    {
      question: "Where can I park?",
      answer: "Walk if you can—this is a residential area with extremely limited parking. We encourage attendees to walk, carpool, or use alternative transportation whenever possible."
    },
    {
      question: "What entertainment will there be?",
      answer: "We have an exciting lineup of entertainment throughout the day! Enjoy fun dog games like Musical Sits and Red Light Green Light, watch our colorful Paws of Purpose Costume Parade, and see live demonstrations from our Hero Masters showcasing the incredible skills of working and service dogs."
    },
    {
      question: "How can I volunteer?",
      answer: "We'd love your support! Contact us at paws@twra.net for volunteer opportunities and information."
    },
    {
      question: "How do I enter the competitions?",
      answer: "Visit our Competitions page to view all available competitions and register online. Entry fees range from $5-$10, with all proceeds supporting community initiatives."
    },
    {
      question: "What should I bring to the event?",
      answer: "We recommend: poop bags, sunscreen, hat, comfortable shoes, and cash for vendors and competitions. Bring chairs or blankets if you want to relax on the lawn!"
    },
    {
      question: "Are kids welcome?",
      answer: "Absolutely! Children are welcome and encouraged to participate in our Paws of Purpose Kids Colouring Contest. Kids may also participate in dog games and the costume competition, provided they can safely handle and control the dog under adult supervision."
    },
    {
      question: "Will there be a raffle?",
      answer: "Information about our event raffle will be announced soon. Stay tuned for exciting prize details!"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-cyan-50 to-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-4 bg-cyan-100 text-cyan-800 hover:bg-cyan-200 border-cyan-200">
              Need Help?
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-slate-600 mb-8">
              Everything you need to know about Paws in the Park 2026
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq) => (
                <AccordionItem 
                  key={faq.question} 
                  value={faq.question} 
                  className="bg-white border border-slate-200 rounded-lg px-6"
                >
                  <AccordionTrigger className="text-left font-semibold text-slate-800 hover:text-cyan-700">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600">
                    <p>{faq.answer}</p>
                    {faq.showEtiquetteButton && (
                      <div className="mt-4">
                        <Button asChild variant="outline" size="sm" className="border-cyan-200 text-cyan-700 hover:bg-cyan-50">
                          <Link to="/etiquette">
                            View Event Etiquette Guidelines
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Link>
                        </Button>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {/* Contact CTA */}
            <div className="mt-12 text-center">
              <Card className="bg-gradient-to-br from-cyan-50 to-teal-50 border-cyan-200">
                <CardContent className="py-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">
                    Still Have Questions?
                  </h3>
                  <p className="text-slate-600 mb-6">
                    We're here to help! Reach out to us and we'll get back to you as soon as possible.
                  </p>
                  <a 
                    href="/contact" 
                    className="inline-block bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 text-white font-semibold px-8 py-3 rounded-lg transition-all duration-300 hover:shadow-lg"
                  >
                    Contact Us
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
